import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import AuthLayout from '../../layout/AuthLayout';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';

function SignIn() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, loading } = useAuth();

  // Redirect if already authenticated
  useEffect(() => {
    if (!loading && isAuthenticated()) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  // Check for success message and pre-filled email from verification
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
    }
    if (location.state?.email) {
      setFormData(prev => ({
        ...prev,
        email: location.state.email
      }));
    }
    // Clear the state from location
    if (location.state?.message || location.state?.email) {
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, location.pathname]);

  // Show loading while checking authentication
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-primary"></div>
      </div>
    );
  }

  // Don't render form if user is authenticated (will redirect)
  if (isAuthenticated()) {
    return null;
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          email: formData.email, 
          password: formData.password 
        })
      });

      if (!response.ok) {
        let data = {};
        try {
          data = await response.json();
        } catch (e) {
          // ignore JSON parse errors
        }
        const backendMessage = (data && (data.message || data.error || data.detail)) || '';
        if (response.status === 401 || response.status === 403 || /access denied/i.test(backendMessage)) {
          throw new Error(t('signIn.invalidCredentials'));
        }
        throw new Error(backendMessage || t('signIn.loginFailed'));
      }

      const result = await response.json();
      console.log('Login successful!', result);

      // Extract user data from response
      const userData = {
        name: result.user?.name || result.user?.username || result.username || 'User',
        email: result.user?.email || formData.email,
        role: result.user?.role || 'User'
      };

      login(result.token, userData);
      
      // Redirect to the page they were trying to access, or home
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });

    } catch (err) {
      if (err.message === 'Failed to fetch') {
        setError(t('common.serverConnectionError'));
      } else {
        setError(err.message);
      }
    }
  };

  return (
    <AuthLayout 
      title={t('signIn.title')}
      subtitle={t('signIn.subtitle')}
    >
      {/* Form Card */}
      <div className="bg-white py-8 px-6 shadow-lg rounded-lg border border-gray-200">
        <form className="space-y-6" onSubmit={handleSubmit}>
          {/* Email Field */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              {t('auth.email')}
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-primary focus:border-blue-primary"
              placeholder={t('signIn.emailPlaceholder')}
            />
          </div>

          {/* Password Field */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              {t('auth.password')}
            </label>
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                required
                value={formData.password}
                onChange={handleChange}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-primary focus:border-blue-primary"
                placeholder={t('signIn.passwordPlaceholder')}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          {/* Remember Me & Forgot Password */}
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="remember-me"
                name="rememberMe"
                type="checkbox"
                checked={formData.rememberMe}
                onChange={handleChange}
                className="h-4 w-4 text-blue-primary focus:ring-blue-primary border-gray-300 rounded"
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                {t('auth.rememberMe')}
              </label>
            </div>
            <Link
              to="/forgot-password"
              className="text-sm text-blue-primary hover:text-blue-secondary font-medium"
            >
              {t('auth.forgotPassword')}
            </Link>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="text-green-600 text-sm bg-green-50 border border-green-200 rounded-md p-3">
              {successMessage}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-md p-3">
              {error}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!formData.email || !formData.password}
            className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-primary hover:bg-blue-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-primary disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-200"
          >
            {t('signIn.logIn')}
            <ArrowRight className="ml-2 h-4 w-4" />
          </button>
        </form>

        {/* Sign Up Link */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            {t('auth.dontHaveAccount')}{' '}
            <Link
              to="/signup"
              className="font-medium text-blue-primary hover:text-blue-secondary"
            >
              {t('auth.signUp')}
            </Link>
          </p>
        </div>
      </div>
    </AuthLayout>
  );
}

export default SignIn; 